import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import type { AppLocale, Translate } from "../i18n";
import { CheckoutApiError, getOrderStatus, refreshOrderPayment, type OrderPaidStatus } from "../lib/api";
import { formatMoney } from "../lib/money";
import { BRAND } from "../lib/brand";
import { recallTokenForOrder, forgetTokenForOrder } from "../lib/session-token";
import { apiReturnUrl } from "../lib/return-url";
import { flowCopy } from "./flow-copy";
import { BACKGROUND_POLL_DELAY_MS, CONFIRMATION_POLL_DELAYS_MS } from "../lib/pending-retry";

type ConfirmState =
  | { kind: "confirming" }
  | { kind: "cancelled"; status: OrderPaidStatus; returnUrl: string | null }
  | { kind: "confirmed"; status: OrderPaidStatus; returnUrl: string | null }
  | { kind: "pending"; status: OrderPaidStatus; returnUrl: string | null }
  | { kind: "unconfirmed"; orderId: string; returnUrl: string | null };

// Give webhook reconciliation the first four reads (about 11 seconds).
const AUTOMATIC_REFRESH_POLL_INDEX = 4;
// Keep a delayed, authenticated order recoverable beyond the initial wait. Bound
// browser reads; a manual check or reload starts a fresh confirmation run.
const BACKGROUND_POLL_DELAYS_MS = Array<number>(20).fill(BACKGROUND_POLL_DELAY_MS);

function paymentState(status: OrderPaidStatus): "pending" | "authorized" | "paid" | "cancelled" | null {
  if (status.paymentStatus === undefined) return status.paid === true ? "paid" : status.paid === false ? "pending" : null;
  return ["pending", "authorized", "paid", "cancelled"].includes(status.paymentStatus) ? status.paymentStatus : null;
}

export function Success({
  t,
  locale,
  orderId
}: {
  t: Translate;
  locale: AppLocale;
  orderId: string;
  /** Accepted for router symmetry; the success CTAs leave to the marketing site. */
  navigate?: (path: string) => void;
}) {
  const [state, setState] = useState<ConfirmState>({ kind: "confirming" });
  const [confirmationRun, setConfirmationRun] = useState(0);
  const [checkingAgain, setCheckingAgain] = useState(false);
  const refreshNotBefore = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();
    let authoritativeReturnUrl: string | null = null;
    let pendingStatus: OrderPaidStatus | null = null;
    let refreshRequested = false;
    const token = recallTokenForOrder(orderId);
    const manualCheck = confirmationRun > 0;
    if (!token) {
      // No token to confirm with (e.g. opened the link directly). Show a neutral
      // confirmation rather than asserting payment we cannot verify.
      setState({ kind: "unconfirmed", orderId, returnUrl: null });
      setCheckingAgain(false);
      return;
    }

    const wait = (delayMs: number) =>
      new Promise<void>((resolve) => {
        timeoutId = setTimeout(resolve, delayMs);
      });

    // Confirm against /api, never the Stripe redirect. The paid webhook commonly
    // trails the browser redirect. After ~30 seconds, show a truthful pending
    // state and continue checking instead of treating settlement lag as failure.
    void (async () => {
      const delays = manualCheck
        ? [0, ...BACKGROUND_POLL_DELAYS_MS]
        : [...CONFIRMATION_POLL_DELAYS_MS, ...BACKGROUND_POLL_DELAYS_MS];
      for (const [index, delayMs] of delays.entries()) {
        if (delayMs > 0) await wait(delayMs);
        if (cancelled) return;
        try {
          const status = await getOrderStatus(token, controller.signal);
          if (cancelled) return;
          if (status.orderId !== orderId) {
            forgetTokenForOrder(orderId);
            authoritativeReturnUrl = null;
            pendingStatus = null;
            break;
          }
          authoritativeReturnUrl = apiReturnUrl(status.returnUrl);
          // A voided or refunded order: never "do not pay again", never paid.
          const phase = paymentState(status);
          if (phase === "cancelled") {
            setState({ kind: "cancelled", status, returnUrl: authoritativeReturnUrl });
            return;
          }
          if (phase === "paid" || phase === "authorized") {
            // Retain the token in tab-scoped storage. A reload must perform a
            // fresh authenticated status read to resolve the app return again.
            setState({ kind: "confirmed", status, returnUrl: authoritativeReturnUrl });
            return;
          }
          // Unknown/future wire values are not proof of a completed payment.
          if (phase === null) {
            pendingStatus = null;
            break;
          }
          pendingStatus = status;
          if ((manualCheck || index >= AUTOMATIC_REFRESH_POLL_INDEX)
            && !refreshRequested && Date.now() >= refreshNotBefore.current) {
            refreshRequested = true;
            refreshNotBefore.current = Date.now() + 15_000;
            // This only queues a server-selected existing-session reconciliation.
            // Its acceptance is never proof of authorization or capture. Status
            // reads remain authoritative even if this optional wake fails.
            void refreshOrderPayment(token, controller.signal).then((result) => {
              if (cancelled || result.orderId !== orderId) return;
              refreshNotBefore.current = Math.max(refreshNotBefore.current,
                Date.now() + Math.max(15, result.retryAfterSeconds) * 1000);
            }).catch(() => { /* Keep status polling recoverable on transient errors. */ });
          }
        } catch (error: unknown) {
          if (cancelled) return;
          // An invalid/expired credential cannot recover within this polling run.
          if (error instanceof CheckoutApiError && (error.status === 404 || error.status === 410)) {
            forgetTokenForOrder(orderId);
            authoritativeReturnUrl = null;
            pendingStatus = null;
            break;
          }
        } finally {
          if (!cancelled && manualCheck && index === 0) setCheckingAgain(false);
        }
        if (manualCheck || index >= CONFIRMATION_POLL_DELAYS_MS.length - 1) {
          // A transient manual read failure leaves the existing recovery view
          // visible while the bounded background checks continue.
          if (!pendingStatus) {
            if (manualCheck) continue;
            break;
          }
          setState({ kind: "pending", status: pendingStatus, returnUrl: authoritativeReturnUrl });
        }
      }
      if (!cancelled) {
        setState(pendingStatus
          ? { kind: "pending", status: pendingStatus, returnUrl: authoritativeReturnUrl }
          : { kind: "unconfirmed", orderId, returnUrl: authoritativeReturnUrl });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [confirmationRun, orderId]);

  if (state.kind === "confirming") {
    return (
      <main className="co-state">
        <div className="co-spinner" aria-hidden />
        <p>{t("success.confirming")}</p>
      </main>
    );
  }

  if (state.kind === "cancelled") {
    return (
      <main className="co-center">
        <h1>{t("success.cancelledTitle")}</h1>
        <p role="status">{t("success.cancelledBody")}</p>
        <section className="co-card" style={{ width: "100%", maxWidth: 360 }}>
          <div className="co-price-row">
            <span>{t("success.orderNumber")}</span>
            <span>#{state.status.orderReference}</span>
          </div>
        </section>
        <div className="co-stack">
          <a className="co-cta" href={state.returnUrl ?? BRAND.marketingUrl}>
            {state.returnUrl ? t("success.returnToSite") : t("success.back")}
          </a>
          <a className="co-btn-secondary" href={`mailto:${BRAND.supportEmail}`}>
            {t("cancelled.help")}
          </a>
        </div>
      </main>
    );
  }

  if (state.kind === "unconfirmed" || state.kind === "pending") {
    const pending = state.kind === "pending";
    const reference = pending ? state.status.orderReference : `TK-${orderId.replace(/-/g, "").slice(0, 8).toUpperCase()}`;
    const supportUrl = `mailto:${BRAND.supportEmail}?subject=${encodeURIComponent(`${t("chrome.paidTitle")} ${reference}`)}`;
    return (
      <main className="co-center">
        <h1>{t(pending ? "success.pendingTitle" : "success.confirming")}</h1>
        <p role="status">{t(pending ? "success.pendingBody" : "success.notConfirmed")}</p>
        <section className="co-card" style={{ width: "100%", maxWidth: 360 }}>
          <div className="co-price-row">
            <span>{t("success.orderNumber")}</span>
            <span>#{reference}</span>
          </div>
        </section>
        <div className="co-stack">
          <button
            type="button"
            className="co-cta"
            disabled={checkingAgain}
            onClick={() => {
              setCheckingAgain(true);
              setConfirmationRun((run) => run + 1);
            }}
          >
            {t(checkingAgain ? "success.confirming" : "success.checkAgain")}
          </button>
          <a className="co-btn-secondary" href={state.returnUrl ?? BRAND.marketingUrl}>
            {state.returnUrl
              ? t("success.returnToSite")
              : t("success.back")}
          </a>
          <a className="co-btn-secondary" href={supportUrl}>
            {t("cancelled.help")}
          </a>
        </div>
      </main>
    );
  }

  const { status } = state;
  const confirmedReturnUrl = state.returnUrl;
  const trackingToken = status.fulfillment ? recallTokenForOrder(orderId) : null;
  const authorizationOnly = paymentState(status) === "authorized";
  // Design C5: completed = filled teal circle with a check; in-progress = an
  // orange ring holding the brand diamond; upcoming = an empty muted ring.
  const fulfillment = status.fulfillment?.status;
  const productionStarted = ["in_production", "shipped", "delivered", "completed"].includes(fulfillment ?? "");
  const shipped = ["shipped", "delivered", "completed"].includes(fulfillment ?? "");
  const delivered = fulfillment === "delivered" || fulfillment === "completed";
  // Payment authorization/capture does not prove a provider has begun work.
  // Missing fulfillment remains a preview of upcoming steps, and paused or
  // cancelled fulfillment should use its order-status page instead.
  const showTimeline = fulfillment !== "under_review" && fulfillment !== "cancelled";
  const stepStates = [productionStarted ? "completed" : "upcoming",
    shipped ? "completed" : productionStarted ? "pending" : "upcoming",
    delivered ? "completed" : shipped ? "pending" : "upcoming"] as const;
  const steps = (["success.step1", "success.step2", "success.step3"] as const).map((label, index) => {
    const stepState = stepStates[index];
    return { label: t(label), state: stepState,
      icon: stepState === "completed" ? <Check size={13} aria-hidden />
        : stepState === "pending" ? <span className="co-diamond is-small is-orange" aria-hidden /> : null,
      status: t(stepState === "completed" ? "success.statusCompleted"
        : stepState === "pending" ? "success.statusPending" : "success.statusUpcoming") };
  });

  return (
    <main className="co-page" style={{ alignItems: "center" }}>
      {/* The brand mark alone (no wordmark) rather than a stacked
          diamond-over-check glyph, which read as a broken icon. */}
      <div className="co-check" aria-hidden>
        <img className="co-check-logo" src={BRAND.logos.fullColorMark} alt="" />
      </div>
      <h1 style={{ textAlign: "center", margin: 0 }}>
        {t(authorizationOnly ? "success.authorizedTitle" : "success.title")}
      </h1>

      <section className="co-card" style={{ width: "100%" }}>
        <div className="co-price-row">
          <span>{t("success.orderNumber")}</span>
          <span>#{status.orderReference}</span>
        </div>
        {status.totalMinor !== null && status.currency && (
          <div className="co-price-row co-price-total">
            <span>{t(authorizationOnly ? "success.authorizedTotal" : "success.paidTotal")}</span>
            <span className="co-success-amount">
              {formatMoney(status.totalMinor, status.currency, locale)}
            </span>
          </div>
        )}
      </section>

      {showTimeline && <section className="co-card" style={{ width: "100%" }}>
        <h2 className="co-card-title">{t("success.nextTitle")}</h2>
        <div className="co-timeline">
          {steps.map((step, i) => (
            <div className="co-tl-step" key={i}>
              <div className="co-tl-rail">
                <span className="co-tl-dot" data-state={step.state}>
                  {step.icon}
                </span>
                {i < steps.length - 1 && <span className="co-tl-line" />}
              </div>
              <div className="co-tl-body">
                <div className="co-tl-label">{step.label}</div>
                <div className="co-tl-status">{step.status}</div>
              </div>
            </div>
          ))}
        </div>
      </section>}

      <div className="co-stack">
        {trackingToken ? (
          <a className="co-cta" href={`/o/${trackingToken}/status?lang=${locale}`}>{t("success.track")}</a>
        ) : confirmedReturnUrl ? (
          // A valid merchant return URL was passed: the primary CTA goes back to the
          // merchant's own order-status page (same tab). Label stays merchant-agnostic
          // because this is the platform checkout, not any one merchant's surface.
          <a className="co-cta" href={confirmedReturnUrl}>
            {t("success.viewOrder")}
          </a>
        ) : (
          <a className="co-cta" href={BRAND.marketingUrl}>
            {t("success.back")}
          </a>
        )}
        {!confirmedReturnUrl && (
          <a className="co-btn-secondary" href={`mailto:${BRAND.supportEmail}`}>
            {t("cancelled.help")}
          </a>
        )}
        {trackingToken && <a className="co-btn-secondary" href={`/o/${encodeURIComponent(trackingToken)}/privacy?lang=${locale}`}>{flowCopy(locale).privacyLink}</a>}
      </div>
    </main>
  );
}
